import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { ConnectInterventionModal, Dropdown, InterventionModal, SnoozeModal, useCan } from "components";
import { useShowPriceInVat } from "hooks";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { useAppendQuestionResultSnoozeMutation } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/QuestionResultActionSelector/hooks";
import "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/TyreQuestionResults/components/TyreTable/components/TyreTableRow/components/TyreActions/components/TyreActionSelector/TyreActionSelector.scss";
import { useTyreTitleAndSeasons } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/hooks/useTyreTitleAndSeasons";
import { useAppointmentDealer, useAppointmentLocation, useCanConvertToIntervention } from "modules/AppointmentDetails/hooks";
import { convertQuestionResultToIntervention, parseQuestionResultAttachments } from "modules/AppointmentDetails/utils";
import { canWriteBackCustomerData } from "utils/appointmentUtils";

import type { ConnectToInterventionPayload } from "components/ConnectInterventionModal";
import type { Appointment, ChecklistTemplate, QuestionResult, Snooze } from "models";
import type { QuestionResultData } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/types";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";

type TyreActionSelectorProps = {
  questionResult: QuestionResult;
  appointment: Appointment;
  template?: ChecklistTemplate | null;
  readOnly?: boolean;
};

const getDropdownTriggerIcon = (isQuestionResultConvertedOrConnected: boolean, isQuestionResultSnoozed: boolean) => {
  if (isQuestionResultConvertedOrConnected) return "link horizontal grey";
  if (isQuestionResultSnoozed) return "clock grey";
  return "plus grey";
};

export const TyreActionSelector = ({ questionResult, template, appointment, readOnly }: TyreActionSelectorProps) => {
  const { t } = useTranslation();

  const [snoozeQuestionItemPayload, setSnoozeQuestionItemPayload] = useState<QuestionResultData | null>(null);
  const [convertToInterventionPayload, setConvertToInterventionPayload] = useState<AddInterventionPayload | null>(null);
  const [connectInterventionPayload, setConnectInterventionPayload] = useState<ConnectToInterventionPayload | null>(null);

  const isQuestionResultConvertedOrConnected = appointment.interventions?.some(i => i.question_result_id === questionResult.id) ?? false;

  const appointmentLocation = useAppointmentLocation(appointment);
  const appointmentDealer = useAppointmentDealer(appointment);
  const showPriceInVat = useShowPriceInVat();
  const canCreateSnooze = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Snooze);
  const canUpdateIntervention = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.Interventions);
  const canConvertToIntervention = useCanConvertToIntervention(appointmentLocation, isQuestionResultConvertedOrConnected);
  const { tyreTitle, seasonOptions } = useTyreTitleAndSeasons(questionResult.tyre_position);
  const appendQuestionResultSnooze = useAppendQuestionResultSnoozeMutation();

  const canSnooze =
    canCreateSnooze &&
    appointmentLocation?.schedule_enabled &&
    (!template || template.snooze_enabled) &&
    !isQuestionResultConvertedOrConnected &&
    (!questionResult.question_options || questionResult.question_options.find(qo => qo.name === "snooze_enabled" && qo.value));

  const handleConnectToIntervention = () => setConnectInterventionPayload({ question_result_id: questionResult.id, title: questionResult.title });
  const handleConvertToIntervention = () => {
    const convertedInterventionPayload = convertQuestionResultToIntervention({
      title: tyreTitle,
      questionResult,
      appointment,
      seasonOptions,
      location: appointmentLocation,
      showPriceInVat
    });

    setConvertToInterventionPayload(convertedInterventionPayload);
  };

  const handleSnoozeQuestionItem = () =>
    setSnoozeQuestionItemPayload({
      title: questionResult.title,
      solution: questionResult.solution,
      description: questionResult.raw,
      question_result_id: questionResult.id,
      appointment_id: questionResult.appointment_id,
      snooze_history: questionResult.snooze_history,
      attachments: parseQuestionResultAttachments(questionResult)
    });

  const handleCheckAppendSnooze = async (snooze: Snooze) => {
    if (!canCreateSnooze) return;
    await appendQuestionResultSnooze.mutateAsync(snooze);
  };

  return (
    <>
      <div className="TyreActionSelector">
        <Dropdown
          disabled={readOnly || questionResult.snoozed || isQuestionResultConvertedOrConnected}
          className="action-dropdown"
          floating
          icon={null}
          direction="left"
          trigger={<Icon className={getDropdownTriggerIcon(isQuestionResultConvertedOrConnected, questionResult.snoozed)} />}
        >
          <DropdownMenu className="dropdown-menu">
            {canConvertToIntervention && (
              <DropdownItem onClick={handleConvertToIntervention}>
                <Icon className="screwdriver wrench" />
                {t("v8_convert_to_intervention", "Convert to Intervention")}
              </DropdownItem>
            )}

            {canUpdateIntervention && (
              <DropdownItem onClick={handleConnectToIntervention}>
                <Icon className="link horizontal" />
                {t("v8_connect_to_intervention", "Connect to Intervention")}
              </DropdownItem>
            )}

            {canSnooze && (
              <DropdownItem onClick={handleSnoozeQuestionItem}>
                <Icon className="clock" />
                {t("v8_snooze", "Snooze")}
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      <InterventionModal
        location={appointmentLocation}
        dealer={appointmentDealer}
        appointmentId={appointment.id}
        carId={appointment.car_id}
        open={convertToInterventionPayload !== null}
        intervention={convertToInterventionPayload}
        onClose={() => setConvertToInterventionPayload(null)}
        canWriteBackCustomerData={canWriteBackCustomerData(appointmentLocation)}
      />

      <ConnectInterventionModal
        connectToInterventionPayload={connectInterventionPayload}
        open={connectInterventionPayload !== null}
        onClose={() => setConnectInterventionPayload(null)}
        appointment={appointment}
        checklistName={questionResult.checklist_name || ""}
      />

      {snoozeQuestionItemPayload && (
        <SnoozeModal
          dealer={appointmentDealer}
          location={appointmentLocation}
          open={true}
          onClose={() => setSnoozeQuestionItemPayload(null)}
          closeOnDimmerClick
          data={snoozeQuestionItemPayload}
          onAppendSnooze={handleCheckAppendSnooze}
          isAppendSnoozeLoading={appendQuestionResultSnooze.isPending}
        />
      )}
    </>
  );
};
